/** Purpose: gather localization keys from nested node data. Why: key search must find mappings even when they are inside complex node payloads. */
const collectLocalizationKeys = (value, keys = []) => {
  if (!value || typeof value !== 'object') return keys;
  if (Array.isArray(value)) {
    value.forEach((item) => collectLocalizationKeys(item, keys));
    return keys;
  }

  Object.entries(value).forEach(([property, nested]) => {
    if (property === 'localizationKey' && typeof nested === 'string' && nested.trim()) {
      keys.push(nested.trim());
    } else if (nested && typeof nested === 'object') {
      collectLocalizationKeys(nested, keys);
    }
  });
  return keys;
};

/** Purpose: score one node against a search query. Why: exact/prefix localization-key matches should rank ahead of general text matches. */
const getNodeSearchScore = (node, query, savedFunctions) => {
  const normalizedQuery = String(query || '').trim().toLocaleLowerCase();
  if (!normalizedQuery) return -1;

  /** Purpose: Resolve node functions for the flow node search module. Why: The surrounding operation uses this value to make its decision. */
  const nodeFunctions = savedFunctions.filter((fn) => fn.functionId === node.data?.config?.functionDefinitionId);
  const searchableText = JSON.stringify([node.data, nodeFunctions]).toLocaleLowerCase();
  if (!searchableText.includes(normalizedQuery)) return -1;

  // Localization mappings can live in the node config or in nested node-specific
  // structures (for example, choices and other interactive node payloads).
  /** Purpose: Resolve localization keys in the flow node search module. Why: This keeps the surrounding interface operation aligned with the expected data shape. */
  const localizationKeys = collectLocalizationKeys(node.data).map((key) => key.toLocaleLowerCase());
  if (localizationKeys.some((key) => key === normalizedQuery)) return 1000;
  if (localizationKeys.some((key) => key.startsWith(normalizedQuery))) return 800;
  if (localizationKeys.some((key) => key.includes(normalizedQuery))) return 600;
  return 100;
};

// Preserve the canvas order for equally relevant matches.
/** Purpose: return flow nodes matching text, localization keys, or attached saved functions. Why: editors need one ranked search path that preserves canvas order for ties. */
export const searchFlowNodes = (nodes = [], query = '', savedFunctions = []) => nodes
  .map((node, index) => ({ node, index, score: getNodeSearchScore(node, query, savedFunctions) }))
  .filter((result) => result.score >= 0)
  .sort((a, b) => b.score - a.score || a.index - b.index)
  .map(({ node }) => node);
